import {
  createFileRoute,
  Outlet,
  useNavigate,
  redirect,
  useLocation,
} from '@tanstack/react-router';
import { useState, useEffect, useRef } from 'react';
import { trpc, queryClient } from '../trpc';
import { InitialBillingModal } from '../components/InitialBillingModal';
import { TwoMinuteOnboarding } from '../components/TwoMinuteOnboarding';
import { OnboardingJobPreferencesDialog } from '../components/OnboardingJobPreferencesDialog';
import { OnboardingResumeUploadDialog } from '../components/OnboardingResumeUploadDialog';
import { billingStorageService } from '../utils/billingStorage';
import { useUser } from '@clerk/clerk-react';
import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';
import { toast } from 'sonner';
import { SubscriptionGuard } from '../components/SubscriptionGuard';
import { Footer } from '../components/Footer';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { MobileAppPromotionDialog } from '../components/MobileAppPromotionDialog';
import { AddResumeApplicationButtons } from '@/components/dashboard/AddResumeApplicationButtons';
import { useOrphanedStripeCustomerMonitor } from '../hooks/useOrphanedStripeCustomerMonitor';
import { useStripeCheckoutCancellation } from '../hooks/useStripeCheckoutCancellation';
import { analytics } from '../lib/analytics';

import { waitForClerk } from '../utils/clerk';
import { TourDialog } from '../components/TourDialog';
import { OnboardingTransitionPreview } from '../components/OnboardingTransitionPreview';

export const Route = createFileRoute('/dashboard')({
  component: Dashboard,
  beforeLoad: async () => {
    // Wait for Clerk to load
    const user = await waitForClerk();
    if (!user) {
      return redirect({
        to: '/',
        replace: true,
      });
    }
  },
});

function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Check for access errors from session storage
  useEffect(() => {
    const accessError = window.sessionStorage.getItem('access_error');
    if (accessError) {
      toast.error(accessError, {
        duration: 5000,
        position: 'top-center',
        id: 'access-error',
      });
      // Clear the error after displaying it
      window.sessionStorage.removeItem('access_error');
    }
  }, []);

  const [initialBillingModalOpen, setInitialBillingModalOpen] = useState(false);
  const [_onboardingWizardOpen, setOnboardingWizardOpen] = useState(false);
  const [showTransitionPreview, setShowTransitionPreview] = useState(false);
  const [showJobPreferencesDialog, setShowJobPreferencesDialog] =
    useState(false);
  const [showResumeUploadDialog, setShowResumeUploadDialog] = useState(false);
  const [showTourDialog, setShowTourDialog] = useState(false);
  const [extractedPreferences, setExtractedPreferences] = useState<any>(null);

  const { user, isSignedIn } = useUser();
  const userId = user?.id;
  const location = useLocation();

  // Get current tab from the pathname - derived from location which is reactive
  const currentTab = (() => {
    const pathname = location.pathname;
    if (pathname.includes('/applications')) return 'applications';
    if (pathname.includes('/suggested-jobs')) return 'suggested-jobs';
    if (pathname.includes('/analytics')) return 'analytics';
    if (pathname.includes('/profile')) return 'profile';
    return 'feed';
  })();

  const createResumeContextMutation = trpc.resumeContext.create.useMutation();

  // Sync user data with our database
  const syncUserMutation = trpc.users.syncUserData.useMutation({
    onSuccess: () => {
      // Invalidate user queries to refetch with the new data
      queryClient.invalidateQueries({ queryKey: ['users', 'getCurrentUser'] });
      queryClient.invalidateQueries({ queryKey: ['users', 'getUserPlan'] });
    },
    onError: (error: Error) => {
      console.error('Error syncing user data:', error);
      Sentry.captureException(error, {
        tags: { component: 'Dashboard', action: 'syncUserData' },
      });
    },
  });

  // Check if user exists in database (only after sync completes or if we already know they exist)
  const { data: currentUser } = trpc.users.getCurrentUser.useQuery(undefined, {
    enabled: syncUserMutation.isSuccess,
  });

  // Check if user has completed resume context wizard
  const { data: wizardStatus } = trpc.resumeContext.checkCompletion.useQuery(
    { userId: userId || '' },
    {
      enabled: !!userId,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: 'stale', // Only refetch if cache is stale
    },
  );

  // Check if user has valid subscription or is in trial
  const { data: subscription } = trpc.stripe.getActiveSubscription.useQuery(
    undefined,
    {
      enabled: !!userId,
    },
  );

  // When the component mounts, sync the user data (only once)
  useEffect(() => {
    // Track if we've already synced to prevent multiple calls
    let hasRun = false;

    if (user && userId && !hasRun && !syncUserMutation.isLoading) {
      hasRun = true;

      // Detect user's timezone
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      if (process.env.NODE_ENV === 'development') {
        console.log('Detected user timezone:', timezone);
      }

      // Sync user data from Clerk to our database (only if we have an email)
      if (user.primaryEmailAddress?.emailAddress) {
        syncUserMutation.mutate({
          email: user.primaryEmailAddress.emailAddress,
          firstName: user.firstName || undefined,
          lastName: user.lastName || undefined,
          timezone: timezone,
          signupPlatform: 'web',
        });
      }
    }

    // Cleanup function to prevent memory leaks
    return () => {
      hasRun = false;
    };
  }, []);

  // Monitor for partial Stripe customer records (for debugging/monitoring)
  useOrphanedStripeCustomerMonitor({
    currentUser,
    subscription: undefined,
    componentName: 'Dashboard',
  });

  // Handle Stripe checkout cancellation
  useStripeCheckoutCancellation({
    userId,
    componentName: 'Dashboard',
    showToast: toast.info,
    t,
  });

  // Check for first-time user after sync completes successfully
  useEffect(() => {
    if (!syncUserMutation.isSuccess || !currentUser || !userId) return;

    try {
      const userCreatedAt = new Date(currentUser.createdAt);
      const hoursSinceCreation =
        (Date.now() - userCreatedAt.getTime()) / (1000 * 60 * 60);
      const hasSeenModal = billingStorageService.hasSeenModal(userId);

      // Show onboarding wizard for new users immediately (within first 24 hours)
      if (hoursSinceCreation <= 24) {
        const hasSeenOnboarding = localStorage.getItem(
          `onboarding_completed_${userId}`,
        );
        if (!hasSeenOnboarding) {
          return;
        }
      }

      if (
        hoursSinceCreation >= 1 &&
        hoursSinceCreation <= 24 &&
        !hasSeenModal
      ) {
        setInitialBillingModalOpen(true);
      } else {
        checkForBillingReminder(userCreatedAt, userId);
      }
    } catch (error) {
      console.error('Error checking billing modal conditions:', error);
    }
  }, [syncUserMutation.isSuccess, currentUser, userId]);

  // Trigger resume customization wizard for users with valid subscription
  useEffect(() => {
    if (!userId || !wizardStatus) return;

    // Check if user has skipped the wizard
    const hasSkippedWizard = localStorage.getItem(
      `resume_wizard_skipped_${userId}`,
    );
    if (hasSkippedWizard) {
      return; // Don't show wizard if user has skipped it
    }

    // Check if user is in trial period
    const isInTrial =
      currentUser?.trialExpiresAt &&
      new Date(currentUser.trialExpiresAt) > new Date();

    // Determine if user has valid access
    const hasValidAccess =
      subscription?.status === 'active' ||
      subscription?.status === 'trialing' ||
      isInTrial;

    // Show onboarding if the user has never been through it on any platform.
    // Using data === null (no record at all) rather than !completed so that users
    // who finished the flow but skipped the questionnaire are not re-onboarded.
    if (wizardStatus.data === null && hasValidAccess) {
      setShowTransitionPreview(true);
      // Refs gate the analytics so they fire once per onboarding entry, not
      // on every refetch that keeps wizardStatus.data === null.
      if (!onboardingStartedTrackedRef.current) {
        analytics.track('onboarding_started');
        onboardingStartedTrackedRef.current = true;
      }
      if (!transitionPreviewTrackedRef.current) {
        analytics.track('onboarding_transition_viewed', { step: 'transition_preview' });
        transitionPreviewTrackedRef.current = true;
      }
    }
  }, [wizardStatus, subscription, currentUser, userId]);

  const onboardingStartedTrackedRef = useRef(false);
  const transitionPreviewTrackedRef = useRef(false);

  const handleTransitionPreviewContinue = () => {
    setShowTransitionPreview(false);
    setShowResumeUploadDialog(true);
  };

  // Onboarding now ends after the job preferences step (the required activation
  // milestone), which follows the optional resume upload. Show the tour once,
  // otherwise go straight to the feed.
  const finishOnboarding = () => {
    const hasSeenTour =
      userId && localStorage.getItem(`tour_completed_${userId}`);

    if (!hasSeenTour) {
      setShowTourDialog(true);
    } else {
      navigate({ to: '/dashboard/feed', replace: true });
    }
  };

  // Check if we should show billing reminder
  const checkForBillingReminder = (userCreatedAt: Date, userId: string) => {
    try {
      // Check if user has deferred billing setup
      const hasDeferredBilling =
        billingStorageService.hasDeferredBilling(userId);
      const lastReminderShown =
        billingStorageService.getLastReminderShown(userId);

      if (!hasDeferredBilling) {
        return; // User hasn't deferred, no reminders needed
      }

      // Check if we've already shown a reminder today
      const today = new Date().toDateString();
      if (lastReminderShown === today) {
        return; // Already shown reminder today
      }

      // Calculate days since account creation
      const daysSinceCreation = Math.floor(
        (Date.now() - userCreatedAt.getTime()) / (1000 * 60 * 60 * 24),
      );

      // Reminder days from MD file: 7, 10, 12, 13, 14
      const reminderDays = [7, 10, 12, 13, 14];

      if (reminderDays.includes(daysSinceCreation)) {
        setInitialBillingModalOpen(true);
      }
    } catch (error) {
      console.error('Error checking billing reminder:', error);
      // Optionally capture with error monitoring service
    }
  };

  // Welcome email mutations
  const sendWelcomeWithBillingMutation =
    trpc.email.sendWelcomeWithBilling.useMutation({
      onSuccess: () => {
        console.log('Welcome email with billing sent successfully');
      },
      onError: (error: unknown) => {
        console.error('Failed to send welcome email with billing:', error);
      },
    });

  const sendWelcomeNoBillingMutation =
    trpc.email.sendWelcomeNoBilling.useMutation({
      onSuccess: () => {
        console.log('Welcome email without billing sent successfully');
      },
      onError: (error: unknown) => {
        console.error('Failed to send welcome email without billing:', error);
      },
    });

  // Handle initial billing modal responses
  const handleInitialBillingYes = () => {
    // Set localStorage flags
    if (userId) {
      billingStorageService.markModalShown(userId);
      billingStorageService.markLastReminderShown(userId);
    }

    // Send welcome email with billing
    sendWelcomeWithBillingMutation.mutate();

    // TODO: In future, this will open tier selection or Stripe workflow
  };

  const handleInitialBillingNo = () => {
    // Set localStorage flags for deferred billing and reminder tracking
    if (userId) {
      billingStorageService.markModalShown(userId);
      billingStorageService.markBillingDeferred(userId);
    }

    // Send welcome email without billing
    sendWelcomeNoBillingMutation.mutate();

    // TODO: In future, this will set up reminder schedule (now implemented below)
  };

  // Handle onboarding wizard completion
  const handleOnboardingComplete = () => {
    if (userId) {
      localStorage.setItem(`onboarding_completed_${userId}`, 'true');
    }
  };

  const handleJobPreferencesComplete = async () => {
    // Mark as completed in localStorage
    if (userId) {
      localStorage.setItem(
        `job_preferences_onboarding_completed_${userId}`,
        'true',
      );
    }
    setShowJobPreferencesDialog(false);
    setExtractedPreferences(null);

    // Entering keyword/country is the activation milestone — this is the step we
    // measure as "Onboarding Completed".
    analytics.track('onboarding_completed');

    // Persist a resume-context record so the user isn't re-prompted on future
    // logins (both platforms treat checkCompletion.data !== null as "onboarded").
    if (userId) {
      localStorage.setItem(`resume_wizard_skipped_${userId}`, 'true');
      try {
        await createResumeContextMutation.mutateAsync({
          userId,
          persona: 'entry_level',
        });
      } catch (error) {
        console.error('Failed to persist onboarding completion to DB:', error);
        Sentry.captureException(error, {
          tags: { component: 'Dashboard', action: 'createResumeContext' },
        });
      }
    }
    queryClient.invalidateQueries({
      queryKey: ['resumeContext', 'checkCompletion'],
    });

    // Note: resume_upload_skipped is tracked immediately in the resume dialog's
    // handleSkip (resume upload now precedes this step), so nothing to do here.
    finishOnboarding();
  };

  const handleResumeUploadComplete = (suggestedKeywords?: string[] | null) => {
    setShowResumeUploadDialog(false);
    // Seed the Job Preferences form with the keywords scanned from the resume.
    // Null/empty (skipped or nothing found) leaves the form to its own defaults.
    setExtractedPreferences(
      suggestedKeywords && suggestedKeywords.length > 0
        ? { keywords: suggestedKeywords }
        : null,
    );
    setShowJobPreferencesDialog(true);
  };

  const handleTourComplete = () => {
    // Mark tour as completed in localStorage
    if (userId) {
      localStorage.setItem(`tour_completed_${userId}`, 'true');
    }
    setShowTourDialog(false);
    // Navigate to feed
    navigate({ to: '/dashboard/feed', replace: true });
  };

  // DEBUG: Force show initial billing modal (development only)
  function handleDebugShowBillingModal() {
    if (
      process.env.NODE_ENV === 'development' &&
      import.meta.env.VITE_SHOW_DEBUG_BUTTONS === 'true'
    ) {
      console.log('DEBUG: Forcing initial billing modal to show');
      setInitialBillingModalOpen(true);
    }
  }


  // Get recommended jobs count for the Suggested Jobs tab badge
  const recommendedJobsQuery = trpc.jobListings.getRecommendedJobs.useQuery(
    { sortBy: 'newest' },
    {
      retry: 2,
      retryDelay: 500,
      onError: (error: Error) => {
        console.error('Recommended jobs query error:', error);
        Sentry.captureException(error, {
          tags: {
            component: 'Dashboard',
            action: 'recommendedJobsQuery',
            userId,
          },
        });
      },
      enabled: !!userId && (syncUserMutation.isSuccess || !!currentUser),
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      staleTime: 60000,
      refetchInterval: 300000,
    },
  );

  // Redirect to feed tab if visiting /dashboard directly
  useEffect(() => {
    if (window.location.pathname === '/dashboard' && isSignedIn) {
      navigate({ to: '/dashboard/feed', replace: true });
    }
  }, [isSignedIn, navigate]);

  return (
    <SubscriptionGuard>
      <div className='flex flex-col dashboard-container'>
        <style>{`
          .dashboard-container {
            min-height: calc(100vh - 73px);
          }
          @media (min-width: 768px) {
            .dashboard-container {
              min-height: calc(100vh - 60px);
            }
          }
        `}</style>
        <div className='flex-1 p-4'>
          <h1 className='text-2xl font-bold mb-4'>{t('dashboard.title')}</h1>

          {/* Main Content Tabs */}
          <Tabs
            value={currentTab}
            className='mb-4'
            onValueChange={(value) => {
              // Track tab changes
              posthog.capture('dashboard_tab_clicked', {
                user_id: userId,
                tab: value,
              });
              // Navigate to the selected tab
              navigate({ to: `/dashboard/${value}` });
            }}
          >
            {/* Mobile: Dropdown menu for tabs */}
            <div className='block sm:hidden mb-4'>
              <Select
                value={currentTab}
                onValueChange={(value) => {
                  posthog.capture('dashboard_tab_clicked', {
                    user_id: userId,
                    tab: value,
                  });
                  navigate({ to: `/dashboard/${value}` });
                }}
              >
                <SelectTrigger className='w-full'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='feed'>
                    {t('dashboard.feed', 'Feed')}
                  </SelectItem>
                  <SelectItem value='suggested-jobs'>
                    {t('dashboard.suggestedJobs', 'Suggested Jobs')}{' '}
                    {recommendedJobsQuery.data &&
                      `(${recommendedJobsQuery.data.length})`}
                  </SelectItem>
                  <SelectItem value='applications'>
                    {t('dashboard.applications', 'Applications')}
                  </SelectItem>
                  <SelectItem value='analytics'>
                    {t('dashboard.analytics', 'Analytics')}
                  </SelectItem>
                  <SelectItem value='profile'>
                    {t('dashboard.profile', 'Profile')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Desktop/Tablet: Horizontal tabs with buttons on right */}
            <div className='hidden sm:flex flex-col md:flex-row md:justify-between md:items-center gap-3 mb-4'>
              <TabsList id='dashboard-tabs' className='w-full md:w-auto'>
                <TabsTrigger value='feed' className='flex-1 md:flex-none'>
                  {t('dashboard.feed', 'Feed')}
                </TabsTrigger>
                <TabsTrigger
                  value='suggested-jobs'
                  className='flex-1 md:flex-none'
                >
                  {t('dashboard.suggestedJobs', 'Suggested Jobs')}{' '}
                  {recommendedJobsQuery.data && (
                    <span className='ml-2 inline-flex items-center justify-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800'>
                      {recommendedJobsQuery.data.length}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger
                  value='applications'
                  className='flex-1 md:flex-none'
                >
                  {t('dashboard.applications', 'Applications')}
                </TabsTrigger>
                <TabsTrigger value='analytics' className='flex-1 md:flex-none'>
                  {t('dashboard.analytics', 'Analytics')}
                </TabsTrigger>
                <TabsTrigger value='profile' className='flex-1 md:flex-none'>
                  {t('dashboard.profile', 'Profile')}
                </TabsTrigger>
              </TabsList>
              {/* Desktop: Buttons on right */}
              <div className='hidden md:block'>
                <AddResumeApplicationButtons
                  posthog={posthog}
                  userId={userId}
                  handleDebugShowBillingModal={handleDebugShowBillingModal}
                  setOnboardingWizardOpen={setOnboardingWizardOpen}
                  setInitialBillingModalOpen={setInitialBillingModalOpen}
                  queryClient={queryClient}
                  toast={toast}
                  t={t}
                />
              </div>
            </div>

            {/* Mobile/Tablet: Buttons below tabs, stacked vertically */}
            <div className='sm:hidden mb-4'>
              <AddResumeApplicationButtons
                posthog={posthog}
                userId={userId}
                handleDebugShowBillingModal={handleDebugShowBillingModal}
                setOnboardingWizardOpen={setOnboardingWizardOpen}
                setInitialBillingModalOpen={setInitialBillingModalOpen}
                queryClient={queryClient}
                toast={toast}
                t={t}
              />
            </div>

            {/* Tablet: Buttons stacked vertically on right side of tabs */}
            <div className='hidden sm:flex md:hidden flex-col md:flex-row md:justify-between md:items-center gap-3 mb-4'>
              <div className='w-full'>
                <AddResumeApplicationButtons
                  posthog={posthog}
                  userId={userId}
                  handleDebugShowBillingModal={handleDebugShowBillingModal}
                  setOnboardingWizardOpen={setOnboardingWizardOpen}
                  setInitialBillingModalOpen={setInitialBillingModalOpen}
                  queryClient={queryClient}
                  toast={toast}
                  t={t}
                />
              </div>
            </div>

            <Outlet />
          </Tabs>
        </div>
        <div className='shrink-0'>
          <Footer />
        </div>

        {/* Initial billing modal for first-time users */}
        <InitialBillingModal
          open={initialBillingModalOpen}
          onOpenChange={setInitialBillingModalOpen}
          onYes={handleInitialBillingYes}
          onNo={handleInitialBillingNo}
        />

        {/* Two minute onboarding for new users */}
        {/* DISABLED: Temporarily turned off while redesigning frontend */}
        <TwoMinuteOnboarding
          open={false}
          onOpenChange={setOnboardingWizardOpen}
          onComplete={handleOnboardingComplete}
        />

        {/* Onboarding transition preview for new users */}
        <OnboardingTransitionPreview
          open={showTransitionPreview}
          onContinue={handleTransitionPreviewContinue}
        />

        {/* Job preferences onboarding dialog */}
        <OnboardingJobPreferencesDialog
          open={showJobPreferencesDialog}
          onComplete={handleJobPreferencesComplete}
          extractedPreferences={extractedPreferences}
        />

        {/* Resume upload onboarding dialog */}
        <OnboardingResumeUploadDialog
          open={showResumeUploadDialog}
          onComplete={handleResumeUploadComplete}
        />

        {/* Mobile app promotion dialog */}
        <MobileAppPromotionDialog />

        {/* Tour dialog for first-time users */}
        <TourDialog
          open={showTourDialog}
          onOpenChange={setShowTourDialog}
          onComplete={handleTourComplete}
        />
      </div>
    </SubscriptionGuard>
  );
}
